import { type JSX, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getAllOrganizationsWithBasicData } from 'Api/UcrmTprm/UcrmTprmApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID, getFolderDetailsUrl, getManageFolderOriginBreadcrumbSegments, parseManageFolderOrigin } from 'Helpers/URLBuilder/URLBuilder';
import { FolderBreadcrumbSource, FolderTypes, FolderVersion } from 'Models/Folders';
import { OrganizationBase } from 'Models/Organization';
import { DueDiligenceModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';
import { OptionType } from 'Models/Types/GlobalType';

import { ManageFolderForm, ManageFolderFormProps } from '../Components/ManageFolderForm';

interface UrlParams {
    third_party_id?: string;
    folder_id?: string;
}

export enum FolderPageType {
    CREATE_NEW,
    ADD_VERSION,
    MANAGE,
}

export interface ManageFolderProps {
    documentApi: DocumentApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
    pageType: FolderPageType;
}

/** Renders a page where the user can edit an existing folder, add a new version of an existing folder, or create a new folder. */
export const ManageFolder = (props: ManageFolderProps) => {
    const { third_party_id, folder_id } = useParams<keyof UrlParams>() as UrlParams;
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const preselectedOrganizationId = query.get(SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID) ?? undefined;
    const origin = parseManageFolderOrigin(query, props.module);
    const versionExistsAlready = folder_id !== undefined;
    const [errorMessage, setErrorMessage] = useState<string>();
    const [organizations, setOrganizations] = useState<OrganizationBase[]>();
    const [folderTypes, setFolderTypes] = useState<FolderTypes>();
    const cachedData = useCachedData();
    const [currentVersion, setCurrentVersion] = useState<FolderVersion | null | undefined>(versionExistsAlready ? undefined : null);
    const [folderTypeOptions, setFolderTypeOptions] = useState<OptionType[]>();

    const organization = useMemo(() => (currentVersion && organizations ? organizations.find((candidate) => candidate.id === currentVersion.third_party_id) : undefined), [currentVersion, organizations]);

    useEffect(() => {
        const getFolderTypes = async (): Promise<void> => {
            try {
                const response: ResponseModel<FolderTypes> = await props.moduleApi.getFolderTypes();
                setFolderTypes(response.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        const fetchOrganizations = async (): Promise<void> => {
            try {
                const response = await getAllOrganizationsWithBasicData(props.moduleApi);
                setOrganizations(response.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        getFolderTypes();
        fetchOrganizations();
    }, [props.moduleApi, third_party_id]);

    useEffect(() => {
        if (cachedData.users === undefined) {
            return;
        }

        if (!(third_party_id && folder_id)) {
            setCurrentVersion(null);
            return;
        }

        const getEffectiveVersion = async (): Promise<void> => {
            try {
                const response = await props.moduleApi.getAllFolderVersions(third_party_id, folder_id);
                const currentVersion = response.data.current_version;
                setCurrentVersion(currentVersion);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        getEffectiveVersion();
    }, [props.moduleApi, third_party_id, folder_id, cachedData.users, versionExistsAlready]);

    /**
     * Constructs an array of Folder Type options.
     * The Folder Type options remain undefined (causing the page to show the placeholder) until both the current version (if it exists) and the client's configured Type options are fetched.
     *
     * The client's configured Types are always selectable.
     * The current Type of the Folder (if an existing Folder is being managed); if no Types are configured at the client level, then the existing Type is the only option.
     */
    useEffect(() => {
        if (currentVersion === undefined || folderTypes === undefined) {
            return;
        }

        const currentFolderType = currentVersion?.type;
        const clientFolderTypes = folderTypes ? folderTypes.type_set : [];
        let choices: string[];

        if (currentFolderType) {
            choices = clientFolderTypes.includes(currentFolderType) ? clientFolderTypes : [currentFolderType, ...clientFolderTypes];
        } else {
            choices = clientFolderTypes;
        }

        setFolderTypeOptions(choices.map((folderType) => ({ value: folderType, label: folderType })).sort((a, b) => (a.label > b.label ? 1 : -1)));
    }, [currentVersion, folderTypes]);

    /** The leading breadcrumb segments reflect the page the user navigated from; by default, that is the Third-Party Folders listing. */
    const buildBreadcrumb = (currentVersion: FolderVersion | null): JSX.Element => {
        const originSegments = getManageFolderOriginBreadcrumbSegments(origin).map((segment, index) =>
            segment.link ? (
                <BreadcrumbLink key={index} link={segment.link}>
                    {segment.label}
                </BreadcrumbLink>
            ) : (
                <BreadcrumbText key={index}>{segment.label}</BreadcrumbText>
            )
        );

        if (currentVersion && organization) {
            return (
                <Breadcrumb textColor="blue">
                    {originSegments}
                    <BreadcrumbText>{organization.name}</BreadcrumbText>
                    <BreadcrumbLink link={getFolderDetailsUrl(currentVersion.third_party_id, currentVersion.id, { source: FolderBreadcrumbSource.FOLDERS_LISTING, module: origin.module })}>{currentVersion.name}</BreadcrumbLink>
                    <BreadcrumbText>{props.pageType === FolderPageType.ADD_VERSION ? 'Add New Folder Version' : 'Manage Folder'}</BreadcrumbText>
                </Breadcrumb>
            );
        } else {
            return (
                <Breadcrumb textColor="blue">
                    {originSegments}
                    <BreadcrumbText>Create Folder</BreadcrumbText>
                </Breadcrumb>
            );
        }
    };

    const getPageTitle = (currentVersion?: FolderVersion | null): string => {
        if (folder_id && currentVersion) {
            if (props.pageType === FolderPageType.ADD_VERSION) {
                return 'Add New Folder Version';
            } else {
                return 'Manage Folder';
            }
        } else {
            return 'Create Folder';
        }
    };

    if (errorMessage) {
        return <Text>{errorMessage}</Text>;
    } else if (!(organizations && cachedData.users && folderTypeOptions && currentVersion !== undefined)) {
        return <Placeholder />;
    } else if (folderTypeOptions.length === 0) {
        return <Text>At least one folder type must be defined in "Settings" before a folder can be created.</Text>;
    } else {
        const manageFolderFormProps: ManageFolderFormProps = {
            documentApi: props.documentApi,
            folderTypeOptions: folderTypeOptions,
            module: props.module,
            pageType: props.pageType,
            organizations: organizations,
            moduleApi: props.moduleApi,
            currentVersion: currentVersion ?? undefined,
            preselectedOrganizationId: preselectedOrganizationId,
        };

        return (
            <PageLayoutDefault
                headerBreadcrumb={buildBreadcrumb(currentVersion)}
                headerTitle={getPageTitle(currentVersion)}
                body={[
                    {
                        title: 'Folder Details',
                        content: <ManageFolderForm {...manageFolderFormProps} />,
                    },
                ]}
            />
        );
    }
};
